/**
 * Execution-status vocabulary normalizer (shared).
 *
 * The backend mixes Brain ("SUCCESS" / "FAILED"), Step Functions ("SUCCEEDED" /
 * "RUNNING" / "TIMED_OUT" / "ABORTED") and the bespoke "AWAITING_APPROVAL" /
 * "UNKNOWN" returns. Collapse those to a small canonical UI vocabulary so any
 * executions surface only ever has 6 visual states:
 *
 *   running | awaiting | success | failed | aborted | unknown
 *
 * (Mirrors ui-agentic's per-room ExecutionsTab helper; kept here so the shared
 * executions clients + the Rooms-landing inline overview share one vocabulary.)
 */

export const EXECUTION_STATUS_BUCKETS = Object.freeze({
  running: { label: 'Running', tone: 'blue' },
  awaiting: { label: 'Awaiting', tone: 'amber' },
  success: { label: 'Succeeded', tone: 'emerald' },
  failed: { label: 'Failed', tone: 'rose' },
  aborted: { label: 'Aborted', tone: 'slate' },
  unknown: { label: 'Unknown', tone: 'slate' },
});

/** @returns {keyof typeof EXECUTION_STATUS_BUCKETS} */
export function bucketStatus(raw) {
  const v = String(raw || '').trim().toUpperCase();
  if (!v) return 'unknown';
  if (v === 'AWAITING_APPROVAL' || v === 'PENDING_APPROVAL') return 'awaiting';
  if (v === 'RUNNING' || v === 'STARTED') return 'running';
  if (v === 'SUCCESS' || v === 'SUCCEEDED' || v === 'COMPLETED') return 'success';
  if (v === 'FAILED' || v === 'ERROR' || v === 'TIMED_OUT') return 'failed';
  if (v === 'ABORTED' || v === 'CANCELED' || v === 'CANCELLED') return 'aborted';
  return 'unknown';
}

const TONE_CLASSES = Object.freeze({
  blue: 'bg-blue-50 text-blue-700 ring-blue-200',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  rose: 'bg-rose-50 text-rose-700 ring-rose-200',
  slate: 'bg-slate-100 text-slate-700 ring-slate-200',
});

/** Tailwind class string for a small status pill. */
export function statusPillClassName(bucket) {
  const tone = EXECUTION_STATUS_BUCKETS[bucket]?.tone || 'slate';
  return `inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ring-1 ring-inset ${TONE_CLASSES[tone]}`;
}

/** True when the bucket represents an in-flight or awaiting-action run. */
export function isLiveBucket(bucket) {
  return bucket === 'running' || bucket === 'awaiting';
}
